import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { VERDICT_LABEL, VERDICT_COLOR } from "@/lib/format";

export default function RunGateModal({ deal, onClose, onCompleted }) {
  const [gates, setGates] = useState([]);
  const [selectedGate, setSelectedGate] = useState(null);
  const [scores, setScores] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/gates").then(({ data }) => {
      setGates(data.filter((g) => g.is_published && g.is_active));
    });
  }, []);

  const chooseGate = (g) => {
    setSelectedGate(g);
    const init = {};
    g.criteria.forEach((c) => {
      init[c.criteria_id] = {};
    });
    setScores(init);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        gate_id: selectedGate.gate_id,
        scores: selectedGate.criteria.map((c) => {
          const s = scores[c.criteria_id] || {};
          return {
            criteria_id: c.criteria_id,
            score_value: c.criteria_type === "scored" ? Number(s.value) : null,
            pass_fail_result: c.criteria_type === "pass_fail" ? s.value === "pass" : null,
            rubric_selection: c.criteria_type === "rubric" ? s.value : null,
          };
        }),
      };
      const { data } = await api.post(`/deals/${deal.deal_id}/gate-runs`, payload);
      setResult(data);
      toast.success(`Verdict: ${VERDICT_LABEL[data.verdict]}`);
      onCompleted?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to run gate");
    } finally {
      setSubmitting(false);
    }
  };

  const setScore = (cid, value) => setScores((s) => ({ ...s, [cid]: { value } }));

  const allFilled = selectedGate?.criteria.every((c) => {
    const s = scores[c.criteria_id];
    if (!s || s.value === undefined || s.value === "" || s.value === null) return false;
    if (c.criteria_type === "scored") {
      const n = Number(s.value);
      if (Number.isNaN(n) || n < 0 || n > (c.max_score || 0)) return false;
    }
    return true;
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="run-gate-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: "var(--cw-border)" }}>
          <h2 className="cw-h2">Run Gate — {deal.company_name}</h2>
          <button onClick={onClose} data-testid="close-run-gate-btn"><X size={20} /></button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          {!selectedGate ? (
            <>
              <div className="cw-caption mb-3">Select a gate framework to run</div>
              {gates.length === 0 && (
                <div className="text-sm text-[var(--cw-muted)] italic">
                  No published gates available. Publish a gate first.
                </div>
              )}
              <div className="space-y-2">
                {gates.map((g) => (
                  <button key={g.gate_id} onClick={() => chooseGate(g)}
                    className="w-full text-left cw-card p-4 hover:border-[var(--cw-primary)] transition"
                    data-testid={`select-gate-${g.gate_name}`}>
                    <div className="font-semibold">{g.gate_name}</div>
                    <div className="text-xs text-[var(--cw-muted)] mt-1">
                      {g.gate_type === "weighted_scoring" ? "Weighted scoring" : "Hard filter"} · {g.criteria.length} criteria
                    </div>
                  </button>
                ))}
              </div>
            </>
          ) : result ? (
            <div className="text-center py-8">
              <div className="cw-caption uppercase tracking-widest">Verdict</div>
              <div className="text-4xl font-bold mt-2" style={{ color: VERDICT_COLOR[result.verdict] }}
                data-testid="verdict-result">
                {VERDICT_LABEL[result.verdict]}
              </div>
              {result.total_score !== null && result.total_score !== undefined && (
                <div className="text-sm text-[var(--cw-muted)] mt-2">
                  Weighted score: <strong>{result.total_score}</strong> / 100
                </div>
              )}
              <button onClick={onClose} className="cw-btn-primary mt-6" data-testid="close-result-btn">
                Done
              </button>
            </div>
          ) : (
            <>
              <div className="font-semibold mb-3">{selectedGate.gate_name}</div>
              <div className="space-y-4">
                {selectedGate.criteria.map((c) => (
                  <div key={c.criteria_id} className="cw-card p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-medium text-sm">{c.criteria_text}</div>
                      {c.weight ? (
                        <span className="cw-caption">weight: {c.weight}%</span>
                      ) : null}
                    </div>
                    <div className="mt-3">
                      {c.criteria_type === "scored" && (
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            min={0}
                            max={c.max_score}
                            step="0.5"
                            value={scores[c.criteria_id]?.value ?? ""}
                            onChange={(e) => setScore(c.criteria_id, e.target.value)}
                            className="w-24 h-9 px-3 rounded-md border text-sm"
                            style={{ borderColor: "var(--cw-border)" }}
                            data-testid={`score-${c.criteria_id}`}
                          />
                          <span className="text-sm text-[var(--cw-muted)]">/ {c.max_score}</span>
                        </div>
                      )}
                      {c.criteria_type === "pass_fail" && (
                        <div className="flex gap-4 text-sm">
                          {["pass", "fail"].map((v) => (
                            <label key={v} className="flex items-center gap-1 cursor-pointer">
                              <input type="radio" name={c.criteria_id} value={v}
                                checked={scores[c.criteria_id]?.value === v}
                                onChange={() => setScore(c.criteria_id, v)}
                                data-testid={`pf-${c.criteria_id}-${v}`} />
                              <span className="capitalize">{v}</span>
                            </label>
                          ))}
                        </div>
                      )}
                      {c.criteria_type === "rubric" && (
                        <select
                          value={scores[c.criteria_id]?.value ?? ""}
                          onChange={(e) => setScore(c.criteria_id, e.target.value)}
                          className="w-full h-9 px-3 rounded-md border text-sm bg-white"
                          style={{ borderColor: "var(--cw-border)" }}
                          data-testid={`rubric-${c.criteria_id}`}
                        >
                          <option value="">Select…</option>
                          {(c.rubric_options || []).map((o) => (
                            <option key={o} value={o}>{o}</option>
                          ))}
                        </select>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {selectedGate && !result && (
          <div className="border-t px-6 py-4 flex justify-between" style={{ borderColor: "var(--cw-border)" }}>
            <button onClick={() => setSelectedGate(null)} className="cw-btn-ghost">Back</button>
            <button onClick={submit} disabled={!allFilled || submitting}
              className="cw-btn-primary" data-testid="submit-gate-run-btn">
              {submitting ? "Scoring…" : "Submit & Score"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
